import type React from 'react';
import { useCallback, useSyncExternalStore } from 'react';

/**
 * Where the floating call overlay sits — the dialer, the Twilio pad and the
 * ring cards share one corner, and now one anchor an agent can drag by any of
 * their title bars and find again tomorrow.
 *
 * The anchor is the top-left of the column, in pixels from the module's own
 * top-left, and lives in this browser's localStorage: a position is a habit
 * of a desk, not of an account. Null means "where it has always been", the
 * top-right corner the stylesheet puts it in; the module applies a set
 * anchor as CSS variables (see CommsModule and `.ov-moved` in comms.css), so
 * every floating piece reads the same numbers and none of them can drift.
 */
export interface OverlayPoint {
  x: number;
  y: number;
}

const KEY = 'crm.callOverlay.position';

let current: OverlayPoint | null = read();
const listeners = new Set<() => void>();

function read(): OverlayPoint | null {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as unknown;
    if (typeof parsed !== 'object' || parsed === null) return null;
    const { x, y } = parsed as Record<string, unknown>;
    return typeof x === 'number' && typeof y === 'number' && Number.isFinite(x) && Number.isFinite(y)
      ? { x, y }
      : null;
  } catch {
    return null;
  }
}

function publish(next: OverlayPoint | null) {
  current = next;
  for (const listener of listeners) listener();
}

/** Remember it — the next module, the next day. */
export function saveOverlayPosition(point: OverlayPoint | null) {
  publish(point);
  try {
    if (point) localStorage.setItem(KEY, JSON.stringify(point));
    else localStorage.removeItem(KEY);
  } catch {
    // A browser that refuses storage still gets the drag; it just forgets.
  }
}

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};
const snapshot = () => current;

/** The anchor now, and on every change. */
export function useOverlayPosition(): OverlayPoint | null {
  return useSyncExternalStore(subscribe, snapshot, snapshot);
}

/**
 * Keep a box inside its bounds, with a margin. A box taller than the bounds
 * pins to the top rather than the bottom, so its title bar — the only handle —
 * is the part that stays reachable.
 */
export function clampOverlay(
  point: OverlayPoint,
  box: { width: number; height: number },
  bounds: { width: number; height: number },
  margin = 8,
): OverlayPoint {
  const maxX = Math.max(margin, bounds.width - box.width - margin);
  const maxY = Math.max(margin, bounds.height - box.height - margin);
  return {
    x: Math.round(Math.min(Math.max(point.x, margin), maxX)),
    y: Math.round(Math.min(Math.max(point.y, margin), maxY)),
  };
}

/**
 * The handle. Put its `onPointerDown` on a title bar and the element that bar
 * belongs to (`.rcdial`, `.callring-stack`) follows the pointer, clamped to the
 * module, and the anchor is saved on release. Buttons inside the bar keep
 * their clicks: a press that lands on one is not a drag.
 */
export function useOverlayDrag(): { onPointerDown: (event: React.PointerEvent<HTMLElement>) => void } {
  const onPointerDown = useCallback((event: React.PointerEvent<HTMLElement>) => {
    if (event.button !== 0) return;
    if ((event.target as HTMLElement).closest('button, a, input')) return;
    const handle = event.currentTarget;
    const box = handle.closest<HTMLElement>('.rcdial, .callring-stack');
    const container = handle.closest<HTMLElement>('.comms');
    if (!box || !container) return;
    event.preventDefault();

    const boxRect = box.getBoundingClientRect();
    const start = { x: event.clientX, y: event.clientY };
    const origin = {
      x: boxRect.left - container.getBoundingClientRect().left,
      y: boxRect.top - container.getBoundingClientRect().top,
    };
    const size = { width: boxRect.width, height: boxRect.height };
    let last: OverlayPoint | null = null;

    const move = (pointer: PointerEvent) => {
      const bounds = container.getBoundingClientRect();
      last = clampOverlay(
        { x: origin.x + pointer.clientX - start.x, y: origin.y + pointer.clientY - start.y },
        size,
        { width: bounds.width, height: bounds.height },
      );
      publish(last);
    };
    const up = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      window.removeEventListener('pointercancel', up);
      if (last) saveOverlayPosition(last);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    window.addEventListener('pointercancel', up);
  }, []);
  return { onPointerDown };
}
